import React from 'react';
import PropTypes from 'prop-types';

import { Text } from 'components/DataDisplay';
import { Modal } from 'components/Feedback';
import { FormSection } from 'components/FormSection';
import { Button, Select, TextField } from 'components/Inputs';
import { Container, Item, Row } from 'components/Layout';
import { ListField } from 'components/ListField';

const ErrorState = {
  channelError: false,
  startError: false,
  durationError: false,
  minBudgetError: false,
  formatError: false,
  maxRepeatError: false,
  averageCPMError: false,
  offTargetISCError: false,
  feeError: false,
  memoBetaError: false,
  mandatoryBudgetShareError: false
};

const normalizeOfflineCpmRow = row => {
  if (!row || typeof row !== 'object') {
    return row;
  }
  const next = {
    offlineMediaProxy: row.offlineMediaProxy,
    cpm: row.cpm
  };
  if (row._id != null) {
    next._id = row._id;
  }
  return next;
};

const normalizeOfflineCpmRows = rows => {
  const validRows = Array.isArray(rows) ? rows : [];
  return validRows.map(normalizeOfflineCpmRow);
};

const buildInitialStateFromConfig = config => {
  let mandatoryBudgetShare = '';
  const forced = config.initialForcedChannel;
  if (forced && forced?.percent >= 0) {
    mandatoryBudgetShare = String(Number(forced.percent) * 100);
  }

  const feeRef = config.fee && config.fee._id ? config.fee._id : config.fee;
  const feeKey = config?.fees?.find(el => String(el._id) === String(feeRef))?.key || null;

  return {
    channel: config.channel,
    start: config.start,
    duration: config.duration,
    minBudget: config.minBudget,
    format: config.format === 'none' ? 'aucun' : config.format,
    maxRepeat: config.maxRepeat,
    averageCPM: config.averageCPM,
    offTargetISC: config.offTargetISC * 100,
    fee: feeKey,
    fees: config.fees,
    memoBeta: config.memoBeta,
    mandatoryBudgetShare,
    offlineMediaProxies: config.offlineMediaProxies || []
  };
};

export const ModalForm = props => {
  const { onValidate, config, actionBeta, canEdit, onClose } = props;
  const [
    {
      channel,
      start,
      duration,
      minBudget,
      format,
      maxRepeat,
      averageCPM,
      offTargetISC,
      fee,
      fees,
      memoBeta,
      mandatoryBudgetShare,
      offlineMediaProxies
    },
    setState
  ] = React.useState(() => buildInitialStateFromConfig(config));
  const [offlineCpmRows, setOfflineCpmRows] = React.useState(() =>
    normalizeOfflineCpmRows(config.initialOfflineCpmRows || [])
  );
  const [
    {
      channelError,
      startError,
      durationError,
      minBudgetError,
      formatError,
      maxRepeatError,
      averageCPMError,
      offTargetISCError,
      feeError,
      memoBetaError,
      mandatoryBudgetShareError
    },
    setError
  ] = React.useState(ErrorState);
  const [mandatoryBudgetShareErrorText, setMandatoryBudgetShareErrorText] = React.useState(null);
  const [offlineCpmListError, setOfflineCpmListError] = React.useState(null);

  const toggleError = (itemKey, state) => {
    setError(prevState => {
      const errors = { ...prevState };
      errors[`${itemKey}Error`] = state;
      return errors;
    });
  };

  const resetErrors = () => {
    setError(ErrorState);
    setMandatoryBudgetShareErrorText(null);
    setOfflineCpmListError(null);
  };
  const handleChange = (field, itemKey) => value => {
    const text = value.trim();
    toggleError(itemKey, !text);
    setState(prevState => {
      const states = { ...prevState };
      states[itemKey] = text;
      return states;
    });
  };

  const handleMandatoryBudgetShareChange = value => {
    const text = value.trim();
    setMandatoryBudgetShareErrorText(null);
    toggleError('mandatoryBudgetShare', false);
    setState(prevState => ({
      ...prevState,
      mandatoryBudgetShare: text
    }));
  };

  const resetStates = () => {
    setState(buildInitialStateFromConfig(config));
    setOfflineCpmRows(normalizeOfflineCpmRows(config.initialOfflineCpmRows || []));
  };

  const validateOfflineCpmRows = (rows, channelDbId) => {
    if (!rows.length) {
      return { ok: true, message: null };
    }
    if (!channelDbId) {
      return {
        ok: false,
        message:
          'Impossible de résoudre le canal pour enregistrer les CPM offline. Vérifiez la configuration des canaux.'
      };
    }
    const audiences = [];
    for (let i = 0; i < rows.length; i += 1) {
      const row = rows[i];
      const audience = typeof row.offlineMediaProxy === 'string' ? row.offlineMediaProxy.trim() : '';
      const cpmRaw = row.cpm;
      const cpmStr = cpmRaw === undefined || cpmRaw === null ? '' : String(cpmRaw).trim();
      if (!audience) {
        return {
          ok: false,
          message: 'Veuillez sélectionner une audience cible pour activer le CPM Offline.'
        };
      }
      if (!cpmStr) {
        return {
          ok: false,
          message: 'Le champ CPM est obligatoire lorsque la section CPM Offline est active.'
        };
      }
      const cpmNum = Number(cpmStr);
      if (Number.isNaN(cpmNum)) {
        return {
          ok: false,
          message: 'Le champ CPM est obligatoire lorsque la section CPM Offline est active.'
        };
      }
      if (cpmNum < 0) {
        return {
          ok: false,
          message: 'Le montant du CPM doit être supérieur ou égal à 0.'
        };
      }
      if (audiences.includes(audience)) {
        return { ok: false, message: 'Deux lignes ne peuvent pas utiliser la même audience cible.' };
      }
      audiences.push(audience);
    }
    return { ok: true, message: null };
  };

  const doAction = async () => {
    let error = false;
    resetErrors();
    if (!channel) {
      toggleError('channel', true);
      error = true;
    }
    if (!start) {
      toggleError('start', true);
      error = true;
    }
    if (!duration) {
      toggleError('duration', true);
      error = true;
    }
    if (!minBudget) {
      toggleError('minBudget', true);
      error = true;
    }
    if (!format) {
      toggleError('format', true);
      error = true;
    }
    if (!maxRepeat) {
      toggleError('maxRepeat', true);
      error = true;
    }
    if (!averageCPM) {
      toggleError('averageCPM', true);
      error = true;
    }
    if (!offTargetISC) {
      toggleError('offTargetISC', true);
      error = true;
    }
    if (!fee) {
      toggleError('fee', true);
      error = true;
    }
    if (!memoBeta) {
      toggleError('memoBeta', true);
      error = true;
    }

    if (mandatoryBudgetShare.trim() !== '') {
      const value = Number(mandatoryBudgetShare);
      if (Number.isNaN(value)) {
        toggleError('mandatoryBudgetShare', true);
        setMandatoryBudgetShareErrorText(
          'Veuillez saisir un pourcentage valide (entre 0 et 100).'
        );
        error = true;
      } else if (value < 0 || value > 100) {
        toggleError('mandatoryBudgetShare', true);
        setMandatoryBudgetShareErrorText('Veuillez saisir un pourcentage valide (entre 0 et 100).');
        error = true;
      }
    }

    const offlineValidation = validateOfflineCpmRows(offlineCpmRows, config.channelDbId);
    if (!offlineValidation.ok) {
      setOfflineCpmListError(offlineValidation.message);
      error = true;
    }

    if (!error) {
      const constraintDatas = {
        channel,
        start,
        duration,
        minBudget,
        format: format === 'aucun' ? 'none' : format,
        maxRepeat,
        averageCPM,
        offTargetISC: offTargetISC / 100,
        fee: fees.find(el => el.key === fee).id,
        memoBeta
      };
      const initialOfflineSnapshot = normalizeOfflineCpmRows(config.initialOfflineCpmRows || []);
      const initialForcedSnapshot = config.initialForcedChannel
        ? { ...config.initialForcedChannel }
        : null;
      try {
        await onValidate({
          constraintChannel: constraintDatas,
          mandatoryBudgetShare,
          offlineCpmRows: normalizeOfflineCpmRows(offlineCpmRows),
          initialForcedChannel: initialForcedSnapshot,
          initialOfflineCpmRows: initialOfflineSnapshot,
          channelKey: config.channelKey,
          channelDbId: config.channelDbId
        });
        resetStates();
      } catch (e) {
        console.error(e);
      }
    }
  };

  const getActions = () => {
    return (
      <Container>
        <Row spacing={1}>
          <Item xs justify="flex-end">
            <Button
              variant="contained"
              size="medium"
              widthSize="medium"
              onClick={canEdit ? doAction : onClose}
            >
              {canEdit ? actionBeta || 'Valider' : 'Fermer'}
            </Button>
          </Item>
        </Row>
      </Container>
    );
  };

  const audienceOptions = offlineMediaProxies.map(p => ({ value: p.name, label: p.name }));

  return (
    <Modal actions={getActions()} {...props}>
      <Container>
        <Row spacing={1}>
          <Item justify="flex-start">
            <FormSection title="Général">
              <Container>
                <Row spacing={0}>
                  <Item xs={6} justify="flex-start">
                    <Container>
                      <Row spacing={0} justify="flex-start">
                        <Item xs={11} justify="flex-start">
                          <TextField
                            title="Canal"
                            small
                            value={channel}
                            disabled
                            onChange={handleChange('text', 'channel')}
                            error={channelError}
                          />
                        </Item>
                      </Row>
                    </Container>
                  </Item>
                  <Item xs={6} justify="flex-start">
                    <Container>
                      <Row spacing={0} justify="flex-start">
                        <Item xs={11} justify="flex-start">
                          <Select
                            title="Format nécessaire"
                            onChange={handleChange('text', 'format')}
                            options={['aucun', 'image', 'video', 'audio']}
                            value={format}
                            error={formatError}
                            small
                            disabled={!canEdit}
                          />
                        </Item>
                      </Row>
                    </Container>
                  </Item>
                </Row>
                <Row spacing={0}>
                  <Item xs={6} justify="flex-start">
                    <Container>
                      <Row spacing={0} justify="flex-start">
                        <Item xs={11} justify="flex-start">
                          <TextField
                            title="CPM moyen"
                            small
                            value={averageCPM}
                            onChange={handleChange('text', 'averageCPM')}
                            error={averageCPMError}
                            format="numberDec"
                            unit="€"
                            disabled={!canEdit}
                          />
                        </Item>
                      </Row>
                    </Container>
                  </Item>
                  <Item xs={6} justify="flex-start">
                    <Container>
                      <Row spacing={0} justify="flex-start">
                        <Item xs={11} justify="flex-start">
                          <Select
                            title="Groupe tarifaire"
                            onChange={handleChange('text', 'fee')}
                            options={fees.map(el => el.key)}
                            value={fee}
                            default="aucun"
                            error={feeError}
                            small
                            disabled={!canEdit}
                          />
                        </Item>
                      </Row>
                    </Container>
                  </Item>
                </Row>
                <Row spacing={0}>
                  <Item xs={6} justify="flex-start">
                    <Container>
                      <Row spacing={0} justify="flex-start">
                        <Item xs={11} justify="flex-start">
                          <TextField
                            title="Taux d’impressions sur cible hors data moyen"
                            small
                            value={offTargetISC}
                            onChange={handleChange('rate', 'offTargetISC')}
                            error={offTargetISCError}
                            format="numberDec"
                            unit="%"
                            disabled={!canEdit}
                          />
                        </Item>
                      </Row>
                    </Container>
                  </Item>
                  <Item xs={6} justify="flex-start">
                    <Container>
                      <Row spacing={0} justify="flex-start">
                        <Item xs={11} justify="flex-start">
                          <TextField
                            title="Beta de mémorisation"
                            small
                            value={memoBeta}
                            onChange={handleChange('rate', 'memoBeta')}
                            error={memoBetaError}
                            format="numberDec"
                            unit="%"
                            disabled={!canEdit}
                          />
                        </Item>
                      </Row>
                    </Container>
                  </Item>
                </Row>
              </Container>
            </FormSection>
          </Item>
        </Row>
        <Row spacing={1}>
          <Item justify="flex-start">
            <FormSection title="Contraintes">
              <Container>
                <Row spacing={0}>
                  <Item xs={6} justify="flex-start">
                    <Container>
                      <Row spacing={0} justify="flex-start">
                        <Item xs={11} justify="flex-start">
                          <TextField
                            title="Délai"
                            required
                            small
                            value={start}
                            onChange={handleChange('text', 'start')}
                            error={startError}
                            format="numberDec"
                            disabled={!canEdit}
                          />
                        </Item>
                      </Row>
                    </Container>
                  </Item>
                  <Item xs={6} justify="flex-start">
                    <Container>
                      <Row spacing={0} justify="flex-start">
                        <Item xs={11} justify="flex-start">
                          <TextField
                            title="Durée minimale"
                            required
                            small
                            value={duration}
                            onChange={handleChange('text', 'duration')}
                            error={durationError}
                            format="numberDec"
                            disabled={!canEdit}
                          />
                        </Item>
                      </Row>
                    </Container>
                  </Item>
                </Row>
                <Row spacing={0}>
                  <Item xs={6} justify="flex-start">
                    <Container>
                      <Row spacing={0} justify="flex-start">
                        <Item xs={11} justify="flex-start">
                          <TextField
                            title="Budget minimum"
                            required
                            small
                            value={minBudget}
                            onChange={handleChange('text', 'minBudget')}
                            error={minBudgetError}
                            format="numberDec"
                            unit="€"
                            disabled={!canEdit}
                          />
                        </Item>
                      </Row>
                    </Container>
                  </Item>
                  <Item xs={6} justify="flex-start">
                    <Container>
                      <Row spacing={0} justify="flex-start">
                        <Item xs={11} justify="flex-start">
                          <TextField
                            title="Répétition maximale"
                            required
                            small
                            value={maxRepeat}
                            onChange={handleChange('text', 'maxRepeat')}
                            error={maxRepeatError}
                            format="numberDec"
                            disabled={!canEdit}
                          />
                        </Item>
                      </Row>
                    </Container>
                  </Item>
                </Row>
              </Container>
            </FormSection>
          </Item>
        </Row>
        <Row spacing={1}>
          <Item justify="flex-start">
            <FormSection title="Canal obligatoire">
              <Container>
                <Row spacing={0}>
                  <Item xs={6} justify="flex-start">
                    <Container>
                      <Row spacing={0} justify="flex-start">
                        <Item xs={11} justify="flex-start">
                          <TextField
                            title="Part du budget dans le plan média"
                            small
                            value={mandatoryBudgetShare}
                            onChange={handleMandatoryBudgetShareChange}
                            error={mandatoryBudgetShareError}
                            errorText={mandatoryBudgetShareErrorText}
                            format="numberDec"
                            unit="%"
                            disabled={!canEdit}
                            tooltip="Si ce champ est rempli, alors lorsque les utilisateurs souhaiteront créer ou editer un media plan sur ce domaine, alors ce canal sera obligatoire dans le media plan et sa repartition correspondra au pourcentage defini ci contre."
                          />
                        </Item>
                      </Row>
                    </Container>
                  </Item>
                </Row>
              </Container>
            </FormSection>
          </Item>
        </Row>
        <Row spacing={1}>
          <Item justify="flex-start">
            <FormSection title="CPM Offline">
              <Container>
                <Row spacing={0} justify="flex-start">
                  <Item xs={11} justify="flex-start">
                    <ListField
                      values={offlineCpmRows}
                      options={['offlineMediaProxy', 'cpm']}
                      labels={['Audience cible - contactabilité', 'CPM']}
                      inputs={['Select', 'TextField']}
                      variant="customFields"
                      fieldsFormat={[audienceOptions, 'numberDec']}
                      units={[null, '€']}
                      hideKeys={['_id']}
                      tooltips={[
                        "L'audience selectionné permet d'etablir la contactabilité de ce canal lorsqu'il est en offline. Ces dernières sont prédefinies.",
                        ''
                      ]}
                      deleteAction
                      onChange={next => {
                        setOfflineCpmListError(null);
                        setOfflineCpmRows(normalizeOfflineCpmRows(next));
                      }}
                      actionTxt="Ajouter une ligne"
                      searchOptionsKeys={['offlineMediaProxy', 'cpm']}
                      disabled={!canEdit}
                    />
                  </Item>
                </Row>
                {offlineCpmListError && (
                  <Row spacing={0} justify="flex-start">
                    <Item xs={11} justify="flex-start">
                      <Text color="error" fontSize="14px">
                        {offlineCpmListError}
                      </Text>
                    </Item>
                  </Row>
                )}
              </Container>
            </FormSection>
          </Item>
        </Row>
      </Container>
    </Modal>
  );
};

ModalForm.defaultProps = {
  config: {},
  actionBeta: null
};
ModalForm.propTypes = {
  onValidate: PropTypes.func.isRequired,
  actionBeta: PropTypes.string,
  config: PropTypes.objectOf(PropTypes.any),
  canEdit: PropTypes.bool,
  onClose: PropTypes.func
};

export default ModalForm;
